import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';

import { escapeHtml, renderDrawerValue } from '../lib/cells';
import { labelFor } from '../data/field-labels';

interface DrawerData {
  visible: boolean;
  title: string;
  subtitle: string;
}

/**
 * 우측 상세 드로어 — 행 클릭으로 열림, 오버레이/X 버튼/Esc로 닫힘.
 *
 * 부모(AdminApp)가 `open(row, subtitle)` 호출 → 드로어가 자체 표시.
 */
export class DetailDrawer extends SolaView<DrawerData> {
  override events = {
    click: {
      '@close': 'handleClose',
      '@overlay': 'handleClose',
    },
  };

  constructor() {
    super();
    this.data = { visible: false, title: '상세 정보', subtitle: '' };
  }

  open(row: Record<string, unknown>, subtitle: string): void {
    this.openWithColumnLabels(row, subtitle, new Map());
  }

  /**
   * 카탈로그 표의 상세 — 열 라벨은 DB 열 주석(columns API)에서. 주석이 없는 열은 손 사전(field-labels) → 열 이름 순(2026-10-20).
   * 한글 라벨의 진실은 DDL 주석 하나(마이그 348 ~ 351) — 요청함 폼 · 제품 상세 스펙 카드와 같은 글이 보인다.
   */
  openWithColumnLabels(row: Record<string, unknown>, subtitle: string, labels: ReadonlyMap<string, string>): void {
    // 헤드라인 컬럼 자동 선택
    const headlineKey = row.option_part != null
      ? 'option_part'
      : row.model != null
        ? 'model'
        : row.id != null
          ? 'id'
          : null;
    this.data.title = headlineKey ? String(row[headlineKey]) : subtitle || '상세 정보';
    this.data.subtitle = subtitle;
    this.refreshHeader();
    this.renderBody(row, labels);
    this.show();
  }

  /**
   * 자유 형식 본문 HTML 을 직접 주입.
   * lead/RFQ 같은 풍부한 상세 (관계 데이터 포함) 표시용.
   */
  openWithBody(title: string, subtitle: string, bodyHtml: string): void {
    this.data.title = title;
    this.data.subtitle = subtitle;
    this.refreshHeader();
    const body = this._rootElement?.querySelector('.drawer-body');
    if (body) body.innerHTML = bodyHtml;
    this.show();
  }

  close(): void {
    this.data.visible = false;
    const root = this._rootElement;
    if (!root) return;
    root.classList.add('hidden');
    root.setAttribute('aria-hidden', 'true');
  }

  private show(): void {
    this.data.visible = true;
    const root = this._rootElement;
    if (!root) return;
    root.classList.remove('hidden');
    root.setAttribute('aria-hidden', 'false');
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <aside class="drawer hidden" aria-hidden="true">
        <div class="drawer-overlay" data-action="overlay"></div>
        <div class="drawer-panel">
          <header class="drawer-header">
            <div>
              <h2 class="drawer-title"></h2>
              <span class="drawer-subtitle"></span>
            </div>
            <button class="btn-icon" data-action="close" title="닫기">✕</button>
          </header>
          <div class="drawer-body"></div>
        </div>
      </aside>
    `;
  }

  private refreshHeader(): void {
    const root = this._rootElement;
    if (!root) return;
    const t = root.querySelector('.drawer-title');
    if (t) t.textContent = this.data.title;
    const s = root.querySelector('.drawer-subtitle');
    if (s) s.textContent = this.data.subtitle;
  }

  private renderBody(row: Record<string, unknown>, labels: ReadonlyMap<string, string>): void {
    const body = this._rootElement?.querySelector('.drawer-body');
    if (!body) return;
    const entries = Object.entries(row);
    const rows = entries
      .map(
        ([k, v]) => `
          <div class="kv-row">
            <div class="kv-key">${escapeHtml(labelOf(labels, k))} <span class="kv-key-raw">${escapeHtml(k)}</span></div>
            <div class="kv-val">${renderDrawerValue(v)}</div>
          </div>
        `,
      )
      .join('');
    body.innerHTML = `<div class="drawer-section">${rows}</div>`;
  }

  handleClose(): void {
    this.close();
  }
}

/** 열 라벨 — DB 주석(labels) → 손 사전(field-labels) → 열 이름 */
function labelOf(labels: ReadonlyMap<string, string>, key: string): string {
  const fromComment = labels.get(key);
  return fromComment === undefined ? labelFor(key) : fromComment;
}
